import type { CSSProperties } from "react";
import { useHudStore } from "../store/hudStore";
import { badgesList } from "../store/selectors";
import { ATTRS, BADGES, TITLES, TITLE_LVLS, titleFor, xpNeeded, gemFill } from "../data/seed";
import { Badge, Card, Progress } from "../design-system/components";
import Page from "../components/Page";
import EngineerSprite from "../components/EngineerSprite";
import { bloom } from "../components/ui";
import "./character-vault.css";

export default function Character() {
  const st = useHudStore();
  const empty = !st.live;
  const L = st.quests.filter((q) => !st.done[q.id]);
  const level = empty ? 1 : st.level;
  const xp = empty ? 0 : st.xp;
  const need = xpNeeded(level);
  const nextTitle = (() => {
    const nx = TITLE_LVLS.find((l) => l > level);
    return nx ? "Next title at Lv " + nx + " · " + (TITLES[nx] ?? "") : "Top title earned";
  })();
  const attrSrc = st.live && Array.isArray(st.liveAttrs) ? st.liveAttrs : ATTRS;
  const attrCards = attrSrc.map((a) => {
    const isOpen = st.attr === a.name;
    return {
      name: a.name, icon: a.icon, color: a.color, tint: a.tint, level: a.level,
      pct: (a.into / a.next) * 100,
      detail: a.into + " / " + a.next + " XP into level " + (a.level + 1),
      isOpen,
      feeds: (empty ? [] : L.filter((q) => q.attr === a.name).slice(0, 4)).map((q) => ({ title: q.title, xp: q.xp })),
    };
  });
  const BL = badgesList(st);
  const earned = BL.filter((b) => b.earned).length;

  return (
    <Page back="Back to the HUD">
      <Card className="character-hero">
        <div className="character-portrait" aria-hidden="true"><EngineerSprite variant="idle" /></div>
        <div className="character-summary">
          <div className="character-heading">
            <h1>Arlou</h1><Badge tone="accent">Level {level}</Badge>
            <span className="character-title">{titleFor(level)}</span>
          </div>
          <Badge className="character-next-title">{nextTitle}</Badge>
          <Progress value={(xp / need) * 100} label="Experience" valueLabel={`${xp} / ${need} XP`} />
          <p className="character-next-level">{need - xp} to level {level + 1}</p>
        </div>
      </Card>

      <section className="character-attributes" aria-label="Attribute trees">
        {attrCards.map((a) => (
          <button key={a.name} type="button" className="character-attribute ds-focusable" onClick={() => st.openAttr(a.name)} aria-expanded={a.isOpen} style={{ "--attribute-color": a.color, borderColor: a.isOpen ? a.color : undefined } as CSSProperties}>
            <span className="character-attribute-heading">
              <span className="character-attribute-name"><span className="character-attribute-icon" style={{ background: a.tint, color: a.color }}><i aria-hidden="true" className={a.icon} /></span>{a.name}</span>
              <Badge>Lv {a.level}</Badge>
            </span>
            <Progress value={a.pct} aria-label={`${a.name} experience`} className="character-attribute-progress" style={{ "--ds-tone": a.color, "--attribute-glow": bloom(a.color, a.pct) } as CSSProperties} />
            <span className="character-attribute-detail">{a.detail}</span>
            {a.isOpen && (
              <span className="character-attribute-feeds">
                <span className="character-attribute-caption">Feeds this tree</span>
                {a.feeds.map((fq, i) => <span key={i} className="character-attribute-feed"><span>{fq.title}</span><b>+{fq.xp} XP</b></span>)}
                {a.feeds.length === 0 && <span className="character-attribute-detail">No open quests feed this tree.</span>}
              </span>
            )}
          </button>
        ))}
      </section>

      <Card title={<><i aria-hidden="true" className="fa-solid fa-shield-halved" /> Badge case</>} description={`${earned} of ${BADGES.length} earned`} className="character-badge-case">
        <div className="character-badges">
          {BL.map((b) => (
            <button key={b.name} type="button" className={`character-badge ds-focusable${b.earned ? " character-badge--earned" : ""}`} onClick={() => st.openBadge(b.name)}>
              <span className="character-badge-gem" aria-hidden="true">
                <span className="character-badge-facet" style={{ background: b.earned ? gemFill(b.color) : "var(--raise)", boxShadow: b.earned ? "0 0 16px -4px " + b.color + ", inset 1px 1px 0 rgba(214,230,251,.5), inset -1px -1px 4px rgba(6,15,40,.4)" : "inset 1px 1px 0 rgba(214,230,251,.06)" }} />
                <i className={b.fa} style={{ color: b.earned ? "var(--on-accent)" : "var(--mut)" }} />
              </span>
              <span className="character-badge-copy"><span className="character-badge-name">{b.name}</span><span className="character-badge-state">{b.earned ? "Earned" : "Not earned"}</span></span>
            </button>
          ))}
        </div>
      </Card>
    </Page>
  );
}
